import React, { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  activeSubscriberColumns,
  activeSubscriberRow,
  getActiveSubscriberHref,
} from "./tableParams/activeSubscriberColumns";
import ProjectAPI from "../../../api/ProjectAPI";
import StatsAPI from "../../../api/StatsAPI";
import { navigatorLanguage } from "../../../i18n";
import { getLanguageUuid } from "../../../utils/data/Content";
import DataTable from "../../dataTable/DataTable";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import ExportCsvButton from "../charts/ExportCsvButton";

import type { JSX } from "react";

import type { AnalyticsPeriod } from "@/src/hooks/usePeriodFilter";

import type { ActiveSubscriberRow } from "./tableParams/activeSubscriberColumns.types";
import type { AppState } from "../../../reducers";

interface ActiveSubscribersTableProps {
  period: AnalyticsPeriod;
  userAbility: boolean;
  offerAbility: boolean;
}

function ActiveSubscribersTable({
  period,
  userAbility,
  offerAbility,
}: ActiveSubscribersTableProps): JSX.Element {
  const { t } = useTranslation("editor-analytics");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { startDate, endDate } = period;

  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );
  const { language_uuid: languageUuid } = getLanguageUuid(
    languages,
    navigatorLanguage,
  );

  const getPlatformsQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getPlatforms,
    params: [{ projectSlug }],
  });
  const { data: platformData } = useQuery(getPlatformsQuery);

  const hasPaypalProvider = Boolean(
    platformData?.providers.some(
      (provider) => provider.payment_provider === "paypal",
    ),
  );

  const getActiveSubscriptionsQuery = StatsAPI.generateQuery({
    fn: StatsAPI.getActiveSubscriptions,
    params: [{ projectSlug, startDate, endDate, languageUuid }],
  });
  const {
    data: activeSubscriptions,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...getActiveSubscriptionsQuery,
    enabled: !!projectSlug,
  });

  const rowList = useMemo<ActiveSubscriberRow[]>(
    () =>
      (activeSubscriptions?.data ?? []).map((subscription) =>
        activeSubscriberRow(subscription, t),
      ),
    [activeSubscriptions?.data, t],
  );

  const columnList = useMemo(
    () =>
      activeSubscriberColumns({
        t,
        projectSlug,
        userAbility,
        offerAbility,
        hasPaypalProvider,
      }),
    [t, projectSlug, userAbility, offerAbility, hasPaypalProvider],
  );

  const getRowHref = useCallback(
    (row: ActiveSubscriberRow): string | undefined =>
      getActiveSubscriberHref(row, projectSlug, userAbility),
    [userAbility, projectSlug],
  );

  // Toute la période, côté serveur : sans la recherche ni les filtres.
  const fetchExport = async (): Promise<string> =>
    StatsAPI.getActiveSubscriptions({
      projectSlug,
      startDate,
      endDate,
      languageUuid,
      isExport: true,
    });

  const canExport = !isLoading && rowList.length > 0;

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{t("activeSubscribersTitle")}</CardTitle>
        <CardAction className="flex items-center gap-2">
          <ExportCsvButton
            disabled={!canExport}
            fetchCsv={fetchExport}
            fileName="active_subscriptions.csv"
          />
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnList}
          data={rowList}
          defaultSorting={{ id: "subscription_date", desc: true }}
          emptyText={t("noDatas")}
          getRowHref={getRowHref}
          isError={isError}
          isLoading={isLoading}
          onRetry={refetch}
          resetPageOn={[startDate.valueOf(), endDate.valueOf()]}
          storageKey="transactions-activeSubscribers"
          urlSync={{ prefix: "activeSubscribers" }}
        />
      </CardContent>
    </Card>
  );
}

export default ActiveSubscribersTable;
